import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import './Navbar.css';

const LANGS = [
  { code: 'en', label: 'EN', title: 'English' },
  { code: 'ru', label: 'RU', title: 'Русский' },
  { code: 'uz', label: 'UZ', title: "O'zbekcha" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location]);

  const links = [
    { to: '/', label: t('nav_home') },
    { to: '/courses', label: t('nav_courses') },
    { to: '/teachers', label: t('nav_teachers') },
    { to: '/prices', label: t('nav_prices') },
    { to: '/feedback', label: t('nav_feedback') },
    // One slot, two destinations: signed in it opens the cabinet, signed out it
    // opens the sign-in page. Nobody needs to see both at once.
    user ? { to: '/cabinet', label: t('nav_cabinet') } : { to: '/login', label: t('nav_login') },
  ];

  const goApply = () => {
    setMenuOpen(false);
    setTimeout(() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // The switcher sits outside the collapsing link list on purpose: on a phone a
  // visitor who cannot read the current language should not have to work out
  // that the language lives behind a hamburger. It stays in the bar at every
  // width, the hamburger beside it.
  const switcher = (
    <div className="lang-switcher" role="group" aria-label="Language / Язык / Til">
      {LANGS.map((l, i) => (
        <React.Fragment key={l.code}>
          {i > 0 && <span className="lang-sep" aria-hidden="true">|</span>}
          <button
            type="button"
            lang={l.code}
            title={l.title}
            aria-label={l.title}
            aria-pressed={lang === l.code}
            className={`lang-btn ${lang === l.code ? 'active' : ''}`}
            onClick={() => setLang(l.code)}
          >
            {l.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner container">
        <Link to="/" className="nav-logo">
          <div className="logo-icon">
            {/* Dark ground in both states: over the navy hero, and navy when scrolled. */}
            <Logo size={40} tone="dark" title="SkillUp Academy" />
          </div>
          <div className="logo-text notranslate" translate="no">
            <span className="logo-main">SkillUp</span>
            <span className="logo-sub">Academy</span>
          </div>
        </Link>

        <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {links.map(l => (
            <li key={l.to}>
              <Link to={l.to} className={`nav-link ${location.pathname === l.to ? 'active' : ''}`}>{l.label}</Link>
            </li>
          ))}
          {/* Inside the menu the enrol button is the last thing a thumb reaches;
              in the bar it lives in nav-side instead, so only one of the two is
              ever on screen. */}
          <li className="nav-cta-menu">
            <Link to="/#apply" className="nav-cta" onClick={goApply}>{t('nav_enroll')}</Link>
          </li>
        </ul>

        <div className="nav-side">
          {switcher}
          <Link to="/#apply" className="nav-cta nav-cta-bar" onClick={goApply}>{t('nav_enroll')}</Link>
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </nav>
  );
}
